import { useCallback, useEffect, useRef, useState, type FormEvent } from "react";
import { useParams } from "react-router";
import { api } from "../../data";
import { useSession } from "../../app/session";
import { PublicLayout } from "../../components/shell/WebLayout";
import { KitAnsicht } from "./KitAnsicht";
import { linkBeendet, openKit, openKitAsTeam, type KitTeamOutcome } from "./kit-data";
import { kitAbsender, kitLangVorab, kitTokenLooksValid, tk, type KitLang, type KitOutcome } from "./kit-model";

/**
 * Media-Kit bzw. Pitch-Liste unter /kit/:token (0074). Vor der Prüfung steht nichts Inhaltliches im Dokument; die Marke weist
 * sich mit der Adresse aus, an die der Link ging (nach fünf Fehlversuchen gesperrt). Zurückgezogen, abgelaufen, gesperrt,
 * ungültig: je ein eigener Zustand ohne Daten. Angemeldete Mitarbeiter der Agentur sehen die Teamansicht ohne Protokolleintrag.
 * Sprache: Deutsch oder Englisch umschaltbar; vor dem Öffnen nach der Sprache des Links (?l=en aus Mail und Kopie), sonst
 * nach der Browsersprache; nach dem Öffnen die Sprache des Links (auch für den Umschalter Hell/Dunkel).
 */
export function KitFreigabe() {
  const { token = "" } = useParams();
  return <Freigabe key={token} token={token} />;
}

function Freigabe({ token }: { token: string }) {
  const [outcome, setOutcome] = useState<KitOutcome | null>(() => (kitTokenLooksValid(token) ? null : { kind: "invalid" }));
  const [email, setEmail] = useState("");
  const [geprueft, setGeprueft] = useState("");
  const [busy, setBusy] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [lang, setLang] = useState<KitLang>(() => kitLangVorab(typeof window !== "undefined" ? window.location.search : "", typeof navigator !== "undefined" ? navigator.language : undefined));
  const pending = useRef(false);
  const input = useRef<HTMLInputElement>(null);
  const { session, ready } = useSession();
  const [team, setTeam] = useState<KitTeamOutcome | null>(null);
  const userId = session?.userId ?? null;
  const t = tk(lang);

  // 0425: Vertrag der Agentur beendet: sofort der neutrale Zustand, ohne die Mailadresse abzufragen
  useEffect(() => {
    if (!kitTokenLooksValid(token)) return;
    let alive = true;
    void linkBeendet(api, "kit", token).then((ende) => { if (alive && ende) setOutcome({ kind: "ended" }); });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!ready || !userId || !kitTokenLooksValid(token)) {
      setTeam(null);
      return;
    }
    let alive = true;
    void openKitAsTeam(api, token).then((r) => alive && setTeam(r));
    return () => {
      alive = false;
    };
  }, [ready, userId, token]);

  // Bilder: für die Marke über die geprüfte Adresse (Serverfunktion), fürs Team aus dem eigenen Speicher
  const loadImage = useCallback((talentId: string, assetId?: string) => api.mediaKitImage(token, geprueft, talentId, assetId), [token, geprueft]);
  const loadTeamImage = useCallback(async (talentId: string, assetId?: string) => {
    if (assetId) {
      const a = await api.select<{ path: string }>("talent_kit_assets", { columns: "path", filters: [["id", "eq", assetId], ["talent_id", "eq", talentId]] });
      if (!a[0]?.path) throw new Error("kein Bild");
      return api.download("documents", a[0].path);
    }
    const rows = await api.select<{ profile_image_path: string | null }>("talents", { columns: "profile_image_path", filters: [["id", "eq", talentId]] });
    if (!rows[0]?.profile_image_path) throw new Error("kein Bild");
    return api.download("documents", rows[0].profile_image_path);
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (pending.current) return;
    const value = email.trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
      setFieldError(t("Bitte geben Sie eine gültige E-Mail-Adresse ein.", "Please enter a valid email address."));
      input.current?.focus();
      return;
    }
    setFieldError(null);
    pending.current = true;
    setBusy(true);
    try {
      const next = await openKit(api, token, value);
      if (next.kind === "open") setGeprueft(value.toLowerCase());
      setOutcome(next);
      if (next.kind === "wrong" || next.kind === "failed") input.current?.focus();
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  let content: React.ReactNode;
  if (team?.kind === "team") {
    content = (
      <>
        <div className="notice tk-kit-teamhinweis" role="status">
          Teamansicht: So sieht {team.recipient ?? "die Marke"} diesen Link. Dein Aufruf steht nicht im Protokoll.
          {team.locked && " Die Marke hat den Link durch falsche E-Mail-Adressen gesperrt; lege beim Creator einen neuen an."}
        </div>
        <KitAnsicht view={team.view} loadImage={loadTeamImage} onLang={setLang} />
      </>
    );
  } else if (team?.kind === "revoked" && !outcome) content = <Zustand lang={lang} title="Dieser Link wurde zurückgezogen." text="Die Marke sieht an dieser Stelle nur diesen Hinweis. Einen neuen Link legst du beim Creator oder unter Pitch-Listen an." />;
  else if (team?.kind === "expired" && !outcome) content = <Zustand lang={lang} title="Dieser Link ist abgelaufen." text="Die Marke sieht an dieser Stelle nur diesen Hinweis. Einen neuen Link legst du beim Creator oder unter Pitch-Listen an." />;
  else if (outcome?.kind === "open") content = <KitAnsicht view={outcome.view} loadImage={loadImage} onLang={setLang} />;
  // 0425 (S-7): Vertrag der Agentur beendet: neutral, ohne Agenturnamen
  else if (outcome?.kind === "ended") content = <Zustand lang={lang} title={t("Dieser Link ist nicht mehr verfügbar.", "This link is no longer available.")} text={t("Bitte wenden Sie sich an Ihre Kontaktperson.", "Please contact your contact person.")} />;
  else if (outcome?.kind === "revoked") content = <Zustand lang={lang} title={t("Dieser Link wurde zurückgezogen.", "This link has been withdrawn.")} text={t("Die Agentur hat den Zugang beendet. Bitte wenden Sie sich an Ihre Kontaktperson.", "The agency has ended access. Please contact your contact person.")} />;
  else if (outcome?.kind === "expired") content = <Zustand lang={lang} title={t("Dieser Link ist abgelaufen.", "This link has expired.")} text={t("Bitte fordern Sie bei Ihrer Kontaktperson einen neuen Link an.", "Please ask your contact person for a new link.")} />;
  else if (outcome?.kind === "locked") content = <Zustand lang={lang} title={t("Dieser Link ist gesperrt.", "This link is locked.")} text={t("Nach fünf Versuchen mit einer falschen E-Mail-Adresse ist der Link gesperrt. Bitte fordern Sie einen neuen an.", "After five attempts with a wrong email address the link is locked. Please ask for a new one.")} />;
  else if (outcome?.kind === "unavailable") content = <Zustand lang={lang} title={t("Dieser Link ist derzeit nicht verfügbar.", "This link is currently unavailable.")} text={t("Bitte versuchen Sie es später erneut oder wenden Sie sich an Ihre Kontaktperson.", "Please try again later or contact your contact person.")} />;
  else if (outcome?.kind === "invalid") content = <Zustand lang={lang} title={t("Dieser Link ist ungültig.", "This link is invalid.")} text={t("Bitte öffnen Sie den Link direkt aus der E-Mail. Vielleicht wurde er unvollständig kopiert.", "Please open the link directly from the email. It may have been copied incompletely.")} />;
  else {
    const notice = fieldError
      ?? (outcome?.kind === "wrong"
        ? t(`Diese E-Mail-Adresse passt nicht zum Link. Noch ${outcome.remaining} ${outcome.remaining === 1 ? "Versuch" : "Versuche"}, danach wird der Link gesperrt.`,
          `This email address does not match the link. ${outcome.remaining} ${outcome.remaining === 1 ? "attempt" : "attempts"} left before the link is locked.`)
        : outcome?.kind === "failed"
          ? t("Die Prüfung ist gerade nicht möglich. Bitte versuchen Sie es in einem Moment erneut.", "The check is not possible right now. Please try again in a moment.")
          : null);
    content = (
      <>
        <title>Media-Kit · CreatorOffice</title>
        <form className="centerform panel stack" onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
          <div className="row" role="group" aria-label={t("Sprache", "Language")}>
            <button type="button" aria-pressed={lang === "de"} onClick={() => setLang("de")}>Deutsch</button>
            <button type="button" aria-pressed={lang === "en"} onClick={() => setLang("en")}>English</button>
          </div>
          <h1>Media-Kit</h1>
          <p>{t("Diese Übersicht wurde für Sie freigegeben. Bitte bestätigen Sie mit der E-Mail-Adresse, an die der Link geschickt wurde.",
            "This overview has been shared with you. Please confirm with the email address the link was sent to.")}</p>
          <label>
            {t("E-Mail-Adresse", "Email address")}
            <input
              ref={input} id="kit-mail" name="email" type="email" inputMode="email" enterKeyHint="go" autoComplete="email" autoCapitalize="off"
              spellCheck={false} required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@firma.de"
              aria-invalid={notice ? true : undefined} aria-describedby={notice ? "kit-hinweis" : undefined}
            />
          </label>
          {notice && <p id="kit-hinweis" className="tk-error" role="alert">{notice}</p>}
          <button className="primary" type="submit" disabled={busy} aria-busy={busy}>{busy ? t("Wird geprüft …", "Checking …") : t("Media-Kit ansehen", "View media kit")}</button>
          <small>{t("Der Zugang ist personengebunden. Aufrufe werden protokolliert.", "Access is personal. Views are logged.")}</small>
        </form>
        {api.mode === "demo" && <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictitious.")}</p>}
      </>
    );
  }

  // Nach bestandener Prüfung (bzw. in der Teamansicht) ist die Agentur der Absender im Kopf und im Fuß; davor bleibt er neutral
  // (SICHT-OEFFENTLICH-BETRIEB-28)
  const shown = team?.kind === "team" ? team.view : outcome?.kind === "open" ? outcome.view : null;
  // Inhaber 24.10.2026: gegenüber Marken der Firmenname aus den Firmendaten, sonst der Anzeigename (kitAbsender)
  const sender = shown ? { name: kitAbsender(shown), contact: [shown.agency.email] } : null;
  return (
    <PublicLayout lang={lang} sender={sender}>
      {/* Persönlicher Link: kein Suchindex; der Schlüssel in der Adresse wandert nie als Referrer mit */}
      <meta name="robots" content="noindex, nofollow, noarchive" />
      <meta name="referrer" content="no-referrer" />
      <main id="main" className="auth-shell stack">{content}</main>
    </PublicLayout>
  );
}

function Zustand({ title, text, lang }: { title: string; text: string; lang: KitLang }) {
  return (
    <>
      <title>{`${title} · CreatorOffice`}</title>
      <section className="centerform panel stack" role="status">
        <h1>{title}</h1>
        <p>{text}</p>
        <p>{tk(lang)("Es werden keine Profildaten angezeigt.", "No profile data is shown.")}</p>
      </section>
    </>
  );
}
